import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2026 semantics GmbH
//  Purpose   : reusable color picker panel (wheel, hue/alpha sliders, hex, swatches)
// =============================================================================
// ---Third-party imports
import React, { useEffect, useRef, useState } from 'react';
import { Button, Icon, Input } from 'semantic-ui-react';
import clsx from 'clsx';
// ---Framework imports
import { Sel } from 'uix/registries';
import { useSelector } from 'uix/hooks';
// ---Local imports
import S from './colorPicker.css';
import { XPopup } from './popup';
import { VIcon } from 'uix/graphics';
export const standardColors = [
    // Row 1 — dark
    '#000000',
    '#0747A6',
    '#008DA6',
    '#006644',
    '#FF991F',
    '#BF2600',
    '#403294',
    // Row 2 — mid
    '#97A0AF',
    '#4C9AFF',
    '#00B8D9',
    '#36B37E',
    '#FFC400',
    '#FF5630',
    '#6554C0',
    // Row 3 — light
    '#FFFFFF',
    '#B3D4FF',
    '#B3F5FF',
    '#ABF5D1',
    '#FFF0B3',
    '#FFBDAD',
    '#EAE6FF'
];
//* colorMath functions
const HEX_RE = /^#([A-Fa-f0-9]{3}|[A-Fa-f0-9]{4}|[A-Fa-f0-9]{6}|[A-Fa-f0-9]{8})$/;
const RGBA_RE = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)$/i;
const RGB_TRIPLE_RE = /^\s*(\d{1,3})\s*,\s*(\d{1,3})\s*,\s*(\d{1,3})\s*(?:,\s*([\d.]+)\s*)?$/;
const expandHex = (h)=>h.length <= 4 ? h.split('').map((c)=>c + c).join('') : h;
const hsvToRgb = (h, s, v)=>{
    s /= 100;
    v /= 100;
    let c = v * s;
    let x = c * (1 - Math.abs(h / 60 % 2 - 1));
    let m = v - c;
    let [r, g, b] = h < 60 ? [
        c,
        x,
        0
    ] : h < 120 ? [
        x,
        c,
        0
    ] : h < 180 ? [
        0,
        c,
        x
    ] : h < 240 ? [
        0,
        x,
        c
    ] : h < 300 ? [
        x,
        0,
        c
    ] : [
        c,
        0,
        x
    ];
    return {
        r: Math.round((r + m) * 255),
        g: Math.round((g + m) * 255),
        b: Math.round((b + m) * 255)
    };
};
const rgbToHsv = (r, g, b)=>{
    r /= 255;
    g /= 255;
    b /= 255;
    let max = Math.max(r, g, b);
    let min = Math.min(r, g, b);
    let d = max - min;
    let h = 0;
    if (d !== 0) {
        if (max === r) h = (g - b) / d % 6 * 60;
        else if (max === g) h = ((b - r) / d + 2) * 60;
        else h = ((r - g) / d + 4) * 60;
        if (h < 0) h += 360;
    }
    return {
        h,
        s: max === 0 ? 0 : d / max * 100,
        v: max * 100
    };
};
const hsvToHex = ({ h, s, v, a = 100 })=>{
    let { r, g, b } = hsvToRgb(h, s, v);
    let hex = [
        r,
        g,
        b
    ].map((n)=>n.toString(16).padStart(2, '0')).join('');
    if (a < 100) hex += Math.round(a / 100 * 255).toString(16).padStart(2, '0');
    return `#${hex}`.toUpperCase();
};
const parseColorSyntax = (str)=>{
    /* parse hex (3/4/6/8 digit) and rgb()/rgba() into {h,s,v,a} */ if (!str) return null;
    let s = String(str).trim();
    let m = HEX_RE.exec(s);
    if (m) {
        let hex = expandHex(m[1]);
        let r = parseInt(hex.slice(0, 2), 16);
        let g = parseInt(hex.slice(2, 4), 16);
        let b = parseInt(hex.slice(4, 6), 16);
        let a = hex.length === 8 ? parseInt(hex.slice(6, 8), 16) / 255 : 1;
        return {
            ...rgbToHsv(r, g, b),
            a: Math.round(a * 100)
        };
    }
    m = RGBA_RE.exec(s);
    if (m) {
        let [, r, g, b, a] = m;
        return {
            ...rgbToHsv(+r, +g, +b),
            a: a !== undefined ? Math.round(parseFloat(a) * 100) : 100
        };
    }
    if (s.toLowerCase() === 'transparent') return {
        h: 0,
        s: 0,
        v: 100,
        a: 0
    };
    return null;
};
//* "r, g, b" normally, "r, g, b, a" (a as 0-1 fraction) once opacity is below 100%
const hsvToRgbString = ({ h, s, v, a = 100 })=>{
    let { r, g, b } = hsvToRgb(h, s, v);
    if (a >= 100) return `${r}, ${g}, ${b}`;
    return `${r}, ${g}, ${b}, ${Math.round(a) / 100}`;
};
//* plain "r, g, b[, a]" as shown in the RGB field
const parseRgbString = (str)=>{
    let m = RGB_TRIPLE_RE.exec(String(str ?? ''));
    if (!m) return null;
    let [r, g, b] = m.slice(1, 4).map(Number);
    if ([
        r,
        g,
        b
    ].some((n)=>n > 255)) return null;
    let hsv = rgbToHsv(r, g, b);
    if (m[4] === undefined) return hsv;
    let a = Math.max(0, Math.min(1, parseFloat(m[4])));
    return {
        ...hsv,
        a: Math.round(a * 100)
    };
};
//* resolves anything valid CSS accepts as a color (named colors, hsl(), ...) to rgb()/rgba()
const resolveCssColor = (str)=>{
    if (typeof document === 'undefined') return null;
    let el = document.createElement('div');
    let sentinel = 'rgb(1, 2, 3)';
    el.style.color = sentinel;
    el.style.color = str;
    if (el.style.color === sentinel && str !== sentinel) return null;
    document.body.appendChild(el);
    let resolved = getComputedStyle(el).color;
    document.body.removeChild(el);
    return resolved;
};
const parseColor = (str)=>{
    let hsva = parseColorSyntax(str);
    if (hsva) return hsva;
    let resolved = resolveCssColor(str);
    return resolved ? parseColorSyntax(resolved) : null;
};
const Swatch = ({ color, label, onClick })=>/*#__PURE__*/ _jsx("button", {
        type: "button",
        className: S.swatch,
        style: {
            '--colorPicker_swatch_color': color
        },
        title: label ? `${label} · ${color}` : color,
        onClick: ()=>onClick(color)
    });
//* shared drag handling for the sv square / hue slider / alpha slider:
const useDragAxis = (onMove, onEnd)=>{
    let ref = useRef(null);
    let handlePointerDown = (e)=>{
        e.preventDefault();
        let el = ref.current;
        el.setPointerCapture(e.pointerId);
        let move = (evt)=>{
            let rect = el.getBoundingClientRect();
            let x = Math.min(1, Math.max(0, (evt.clientX - rect.left) / rect.width));
            let y = Math.min(1, Math.max(0, (evt.clientY - rect.top) / rect.height));
            onMove(x, y);
        };
        move(e);
        el.onpointermove = move;
        el.onpointerup = ()=>{
            el.onpointermove = null;
            if (el.hasPointerCapture(e.pointerId)) el.releasePointerCapture(e.pointerId);
            onEnd?.();
        };
    };
    return {
        ref,
        handlePointerDown
    };
};
// ---Exports
export const ColorPicker = ({ value, onPick, onCommit, onRemove, customColors = [], extraSwatches = [], extraLabel })=>{
    let i18n = useSelector(Sel.i18n);
    let [hsva, setHsva] = useState(()=>parseColor(value) || {
            h: 0,
            s: 0,
            v: 0,
            a: 100
        });
    let [hexInput, setHexInput] = useState(()=>{
        let parsed = parseColor(value);
        return parsed ? hsvToHex(parsed).substring(1) : '';
    });
    let [rgbInput, setRgbInput] = useState(()=>hsvToRgbString(hsva));
    let hsvaRef = useRef(hsva);
    let eyedropperInputRef = useRef(null);
    useEffect(()=>{
        let parsed = parseColor(value);
        if (parsed) {
            setHsva(parsed);
            setRgbInput(hsvToRgbString(parsed));
            hsvaRef.current = parsed;
            setHexInput(hsvToHex(parsed).substring(1));
        } else {
            setHexInput('');
        }
    }, [
        value
    ]);
    let commit = (next)=>{
        hsvaRef.current = next;
        setHsva(next);
        let hex = hsvToHex(next);
        setHexInput(hex.substring(1));
        setRgbInput(hsvToRgbString(next));
        onPick(hex);
        return hex;
    };
    let commitFinal = ()=>onCommit?.(hsvToHex(hsvaRef.current));
    let sv = useDragAxis((x, y)=>commit({
            ...hsva,
            s: x * 100,
            v: (1 - y) * 100
        }), commitFinal);
    let hue = useDragAxis((x)=>commit({
            ...hsva,
            h: x * 360
        }), commitFinal);
    let alpha = useDragAxis((x)=>commit({
            ...hsva,
            a: x * 100
        }), commitFinal);
    let handleHex = (str)=>{
        let normalized = str.replace(/^#+/, '');
        setHexInput(normalized);
    };
    let commitHex = ()=>{
        let parsed = parseColorSyntax('#' + hexInput);
        if (parsed) commit(parsed);
        commitFinal();
    };
    let handleAlphaInput = (str)=>{
        let n = Math.max(0, Math.min(100, Number(str) || 0));
        commit({
            ...hsva,
            a: n
        });
    };
    let handleRgb = (str)=>{
        setRgbInput(str);
        let parsed = parseRgbString(str);
        if (parsed) commit({
            ...hsva,
            ...parsed
        });
    };
    let pickSwatch = (color)=>{
        let parsed = parseColor(color);
        if (parsed) {
            commit(parsed);
            commitFinal();
        }
    };
    let hueColor = `hsl(${hsva.h}, 100%, 50%)`;
    let solidHex = hsvToHex({
        ...hsva,
        a: 100
    });
    let usedSwatches = extraSwatches.length > 0 ? extraSwatches : customColors.map((c)=>({
            color: c
        }));
    let openEyedropper = async (e)=>{
        e.stopPropagation();
        let btn = e.currentTarget // React 16 pools the event - grab the node before the await
        ;
        if (typeof window !== 'undefined' && window.EyeDropper) {
            try {
                let { sRGBHex } = await new window.EyeDropper().open();
                commit(parseColorSyntax(sRGBHex));
                commitFinal();
            } catch  {
            // user cancelled the pick - nothing to do
            } finally{
                btn.blur();
            }
        } else {
            eyedropperInputRef.current?.click();
        }
    };
    return /*#__PURE__*/ _jsxs("div", {
        className: S.panel,
        children: [
            usedSwatches.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: S.group,
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        children: extraLabel || i18n.get('editor_color_custom')
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: S.swatches,
                        children: usedSwatches.map(({ color, label })=>/*#__PURE__*/ _jsx(Swatch, {
                                color: color,
                                label: label,
                                onClick: pickSwatch
                            }, `${label || ''}-${color}`))
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                ref: sv.ref,
                className: S.svSquare,
                style: {
                    backgroundColor: hueColor
                },
                onPointerDown: sv.handlePointerDown,
                children: [
                    /*#__PURE__*/ _jsx("div", {
                        className: S.svWhite
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: S.svBlack
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: S.svHandle,
                        style: {
                            left: `${hsva.s}%`,
                            top: `${100 - hsva.v}%`
                        }
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: S.sliderRow,
                children: [
                    /*#__PURE__*/ _jsx(Button, {
                        basic: true,
                        size: "tiny",
                        icon: "eyedropper",
                        className: S.eyedropperBtn,
                        title: i18n.get('editor_btn_custom_font_color_title'),
                        onClick: openEyedropper
                    }),
                    /*#__PURE__*/ _jsx("input", {
                        ref: eyedropperInputRef,
                        type: "color",
                        className: S.hiddenColorInput,
                        value: solidHex,
                        onChange: (e)=>{
                            commit(parseColorSyntax(e.target.value));
                            commitFinal();
                        }
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: S.sliders,
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                ref: hue.ref,
                                className: S.hueSlider,
                                onPointerDown: hue.handlePointerDown,
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: S.sliderHandle,
                                    style: {
                                        left: `${hsva.h / 360 * 100}%`
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                ref: alpha.ref,
                                className: S.alphaSlider,
                                style: {
                                    '--colorPicker_alpha_color': solidHex
                                },
                                onPointerDown: alpha.handlePointerDown,
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: S.sliderHandle,
                                    style: {
                                        left: `${hsva.a}%`
                                    }
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: S.inputRow,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: S.inputLabel,
                        children: "Hex"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        size: "small",
                        className: S.hexInput,
                        placeholder: "#",
                        value: hexInput,
                        onChange: (e, { value: v })=>handleHex(v),
                        onBlur: commitHex,
                        onKeyDown: (e)=>e.key === 'Enter' && commitHex()
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        size: "small",
                        className: S.alphaInput,
                        value: Math.round(hsva.a),
                        onChange: (e, { value: v })=>handleAlphaInput(v),
                        onBlur: commitFinal,
                        onKeyDown: (e)=>e.key === 'Enter' && commitFinal()
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        className: S.percentSign,
                        children: "%"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: S.inputRow,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: S.inputLabel,
                        children: "RGB"
                    }),
                    /*#__PURE__*/ _jsx(Input, {
                        size: "small",
                        className: S.rgbInput,
                        value: rgbInput,
                        onChange: (e, { value: v })=>handleRgb(v),
                        onBlur: commitFinal,
                        onKeyDown: (e)=>e.key === 'Enter' && commitFinal(),
                        placeholder: "0, 0, 0"
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: S.group,
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        children: i18n.get('editor_color_standard_colors')
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: S.swatches,
                        children: standardColors.map((color)=>/*#__PURE__*/ _jsx(Swatch, {
                                color: color,
                                onClick: pickSwatch
                            }, color))
                    })
                ]
            }),
            onRemove && /*#__PURE__*/ _jsxs(Button, {
                basic: true,
                color: "grey",
                size: "small",
                fluid: true,
                onClick: onRemove,
                children: [
                    /*#__PURE__*/ _jsx(VIcon, {
                        name: "trash"
                    }),
                    " ",
                    i18n.get('editor_btn_color_remove')
                ]
            })
        ]
    });
};
export const ColorPickerPopup = ({ open, onOpen, onClose, position = 'bottom left', trigger, ...panelProps })=>/*#__PURE__*/ _jsx(XPopup, {
        on: "click",
        open: open,
        onOpen: onOpen,
        onClose: onClose,
        position: position,
        popperModifiers: [
            {
                name: 'preventOverflow',
                options: {
                    boundary: 'viewport',
                    rootBoundary: 'viewport'
                }
            },
            {
                name: 'flip',
                options: {
                    boundary: 'viewport',
                    rootBoundary: 'viewport'
                }
            }
        ],
        trigger: trigger,
        children: /*#__PURE__*/ _jsx(ColorPicker, {
            ...panelProps
        })
    });
